/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useEffect,
  useMemo,
} from 'react';
import api from 'services';
import formatNumber from 'utils/formatNumber';

import { useDash } from 'hooks/dashboard';
import { useFazenda } from 'hooks/providers/cadastros/fazendas';
import { useNewToast } from 'hooks/newToast';
import { useAuth } from 'hooks/auth';
import { appWarnings } from 'utils/appWarnings';
import { resetRegAtividade } from 'utils/appLocalStorage';

const talhoesDefault = {
  content: [],
  totais: {},
};

const success = {
  message: '',
  type: 'success',
};

const TalhaoContext = createContext({});

export function TalhaoProvider({ children }) {
  const { isAuth } = useAuth();
  const { showToast } = useNewToast();

  const { changeTab } = useDash();

  const [talhoes, setTalhoes] = useState(talhoesDefault);
  const [talhoesReduced, setTalhoesReduced] = useState([]);
  const [talhoesReducedLoading, setTalhoesReducedLoading] = useState(false);
  const [talhoesReducedCoords, setTalhoesReducedCoords] = useState([]);

  const [talhoesLoading, setTalhoesLoading] = useState(true);
  const [talhao, setTalhao] = useState({});
  const [talhaoLoading, setTalhaoLoading] = useState(false);
  const [talhaoRemoveLoading, setTalhaoRemoveLoading] = useState(false);

  const [atividadesByTalhao, setAtividadesByTalhao] = useState({});
  const [atividadesByTalhaoLoading, setAtividadesByTalhaoLoading] =
    useState(false);

  const [insumosTalhao, setInsumosTalhao] = useState([]);
  const [insumosTalhaoLoading, setInsumosTalhaoLoading] = useState(false);

  const [mdobrasTalhao, setMdobrasTalhao] = useState([]);
  const [colheitaTalhao, setColheitaTalhao] = useState([]);
  const [colheitaSimplificadaTalhao, setColheitaSimplificadaTalhao] = useState(
    [],
  );

  const [mdobrasTalhaoLoading, setMdobrasTalhaoLoading] = useState(false);
  const [colheitaTalhaoLoading, setColheitaTalhaoLoading] = useState(false);
  const [
    colheitaSimplificadaTalhaoLoading,
    setColheitaSimplificadaTalhaoLoading,
  ] = useState(false);

  const [caldasTalhao, setCaldasTalhao] = useState([]);
  const [caldasTalhaoLoading, setCaldasTalhaoLoading] = useState(false);

  const [maquinasTalhao, setMaquinasTalhao] = useState([]);
  const [maquinasTalhaoLoading, setMaquinasTalhaoLoading] = useState(false);

  const [insertTalhaoLoading, setInsertTalhaoLoading] = useState(false);

  const [linhasTalhao, setLinhasTalhao] = useState([]);
  const [linhasTalhaoLoading, setLinhasTalhaoLoading] = useState(false);
  const [saveLinhasTalhaoLoading, setSaveLinhasTalhaoLoading] = useState(false);
  const [updateLinhaTalhaoLoading, setUpdateLinhaTalhaoLoading] = useState(false);
  const [deleteLinhaTalhaoLoading, setDeleteLinhaTalhaoLoading] = useState(false);
  const [historicoPlantasLinha, setHistoricoPlantasLinha] = useState([]);
  const [historicoPlantasLinhaLoading, setHistoricoPlantasLinhaLoading] = useState(false);
  const [saveHistoricoPlantasLinhaLoading, setSaveHistoricoPlantasLinhaLoading] = useState(false);
  const [linhasTalhaoPaginadas, setLinhasTalhaoPaginadas] = useState({
    content: [],
    pagination: { total: 0 },
  });

  const [relRegAtividades, setRelRegAtividades] = useState([]);
  const [relRegAtividadesLoading, setRelRegAtividadesLoading] = useState(false);
  const [isCoordLoading, setIsCoordLoading] = useState(false);

  const [relTalhoesDetalhesAtividades, setRelTalhoesDetalhesAtividades] =
    useState([]);
  const [
    relTalhoesDetalhesAtividadesLoading,
    setRelTalhoesDetalhesAtividadesLoading,
  ] = useState(false);

  const [relTalhoesDetalhesDias, setRelTalhoesDetalhesDias] = useState([]);
  const [relTalhoesDetalhesDiasLoading, setRelTalhoesDetalhesDiasLoading] =
    useState(false);

  const { fazendas, setFazendaAtiva } = useFazenda();

  const down = useCallback(() => {
    setTalhoes(talhoesDefault);
    setTalhoesReduced([]);
    setInsumosTalhao([]);
    setMdobrasTalhao([]);
    setCaldasTalhao([]);
    setAtividadesByTalhao([]);
    setRelTalhoesDetalhesAtividades([]);
    setRelTalhoesDetalhesDias([]);
  }, []);

  const getTalhoesReduced = useCallback(async (filters) => {
    try {
      setTalhoesReducedLoading(true);
      const response = await api
        .talhao()
        .getTalhoes({ ...filters, reduced: 1 });
      setTalhoesReduced(response);
      return response;
    } catch (err) {
      return undefined;
    } finally {
      setTalhoesReducedLoading(false);
    }
  }, []);

  const getTalhoes = useCallback(async (filter) => {
    try {
      setTalhoesLoading(true);
      const response = await api.talhao().getTalhoes(filter);
      if (!filter || !filter.reduced) {
        setTalhoes(response);
      } else {
        setTalhoesReducedCoords(response);
      }
    } catch (errors) {
    } finally {
      setTalhoesLoading(false);
    }
  }, []);

  const getTalhao = useCallback(async (id, filter) => {
    try {
      setTalhaoLoading(true);
      const response = await api.talhao().getTalhao(id, filter);
      setTalhao(response);

      setTalhaoLoading(false);
    } catch (errors) {
      setTalhaoLoading(false);
    }
  }, []);

  const insertTalhao = useCallback(
    async (data) => {
      const {
        nome,
        numero,
        fazenda,
        safras,
        produto,
        dataPlantio,
        variedade,
        espX,
        espY,
        areaHa,
        plantasContadas,
        obs,
        coords,
        coordsZoom,
        carreiras,
      } = data.sobre;

      const newSafras = safras.map((safra) => safra.id);

      const newData = {
        nome,
        numero,
        safras: newSafras,
        fazenda: fazenda?.id,
        produto: produto?.id,
        dataPlantio,
        variedade,
        espX,
        espY,
        areaHa: areaHa || 0,
        plantasContadas: formatNumber(plantasContadas || 0),
        obs,
        coords,
        coordsZoom,
        carreiras,
      };

      try {
        setInsertTalhaoLoading(true);
        const response = await api.talhao().insertTalhao(newData);
        setTalhoes((old) => ({ ...old, content: [response] }));
        getTalhoesReduced({ safras: 1 });
        changeTab(2);
        setInsertTalhaoLoading(false);
        resetRegAtividade();
        showToast({ ...success, message: 'Talhão adicionado com sucesso!' });
        return response.id;
      } catch (err) {
        setInsertTalhaoLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error('trazendo error');
      }
    },
    [getTalhoesReduced],
  );

  const insertTalhaoTutorial = useCallback(async (data, safra, fazenda) => {
    try {
      setInsertTalhaoLoading(true);
      const { nome, numero } = data;
      const newData = {
        nome: nome?.toUpperCase(),
        numero,
        fazenda,
        safras: [safra],
        produto: 100,
      };
      const response = await api.talhao().insertTalhao(newData);
      setTalhoes((old) => ({ ...old, content: [response] }));
      resetRegAtividade();
      setInsertTalhaoLoading(false);
      showToast({ ...success, message: 'Talhão adicionado com sucesso!' });
    } catch (err) {
      setInsertTalhaoLoading(false);
      showToast(appWarnings.Danger(err));
      throw new Error('trazendo error');
    }
  }, []);

  const updateTalhao = useCallback(async (id, data) => {
    const {
      nome,
      numero,
      fazenda,
      safras,
      produto,
      dataPlantio,
      variedade,
      espX,
      espY,
      areaHa,
      plantasContadas,
      obs,
      coords,
      coordsZoom,
      carreiras,
    } = data.sobre;

    setInsertTalhaoLoading(true);

    const newSafras = safras.map((safra) => safra.id);

    const newData = {
      nome,
      numero,
      safras: newSafras,
      fazenda: fazenda?.id,
      produto: produto?.id,
      dataPlantio,
      variedade,
      espX,
      espY,
      areaHa,
      plantasContadas,
      obs,
      coords,
      coordsZoom,
      carreiras,
    };

    resetRegAtividade();

    try {
      setTalhaoLoading(true);
      await api.talhao().editTalhao(id, newData);
      const tempFazenda = fazendas.find((item) => item.id === fazenda?.id);
      setFazendaAtiva(tempFazenda);
      setTalhaoLoading(false);
      setInsertTalhaoLoading(false);
      getTalhoes({ fazenda: fazenda?.id });
      showToast({ ...success, message: 'Talhão editado com sucesso!' });
    } catch (err) {
      setTalhaoLoading(false);
      setInsertTalhaoLoading(false);
      showToast(appWarnings.Danger(err));
      throw new Error('trazendo error');
    }
  });

  const removeTalhao = useCallback(async (id) => {
    try {
      setTalhaoRemoveLoading(true);
      await api.talhao().removeTalhao(id);
      resetRegAtividade();
      setTalhoes((old) => ({
        ...old,
        content: old.content?.filter((item) => item.id !== id),
      }));
      setTalhoesReduced((old) => {
        if (!old) return [];
        return old.filter((item) => item.id !== id);
      });
      setTalhaoRemoveLoading(false);
      showToast({ ...success, message: 'Talhão removido com sucesso!' });
    } catch (err) {
      setTalhaoRemoveLoading(false);
      showToast(appWarnings.Danger(err));
      throw new Error();
    }
  }, []);

  const getInsumosByTalhao = useCallback(async (id, filter) => {
    try {
      setInsumosTalhaoLoading(true);
      const response = await api.talhao().getInsumosByTalhao(id, filter);
      setInsumosTalhao(response);
      setInsumosTalhaoLoading(false);
    } catch (err) {
      setInsumosTalhaoLoading(false);
    }
  }, []);

  const getMaquinasByTalhao = useCallback(async (id, filter) => {
    try {
      setMaquinasTalhaoLoading(true);
      const response = await api.talhao().getMaquinasByTalhao(id, filter);
      setMaquinasTalhao(response);
    } finally {
      setMaquinasTalhaoLoading(false);
    }
  }, []);

  const getMdobrasByTalhao = useCallback(async (id, filter) => {
    try {
      setMdobrasTalhaoLoading(true);
      const response = await api.talhao().getMdoByTalhao(id, filter);
      setMdobrasTalhao(response);
    } catch (err) {
    } finally {
      setMdobrasTalhaoLoading(false);
    }
  }, []);

  const getColheitaByTalhao = useCallback(async (id, filter) => {
    try {
      setColheitaTalhaoLoading(true);
      const response = await api.talhao().getColheitaByTalhao(id, filter);
      setColheitaTalhao(response);
      setColheitaTalhaoLoading(false);
    } catch (err) {
      setColheitaTalhaoLoading(false);
    }
  }, []);

  const getColheitaSimplificadaByTalhao = useCallback(async (id, filter) => {
    try {
      setColheitaSimplificadaTalhaoLoading(true);
      const filterWithForce = {
        ...filter,
        force: filter?.force || Date.now(),
      };
      const response = await api
        .talhao()
        .getColheitaSimplificadaByTalhao(id, filterWithForce);
      setColheitaSimplificadaTalhao(response);
      setColheitaSimplificadaTalhaoLoading(false);
    } catch (err) {
      setColheitaSimplificadaTalhaoLoading(false);
    }
  }, []);

  const getCaldasByTalhao = useCallback(async (id, filter) => {
    try {
      setCaldasTalhaoLoading(true);
      const response = await api.talhao().getCaldasByTalhao(id, filter);

      setCaldasTalhao(response);
      setCaldasTalhaoLoading(false);
    } catch (err) {
      setCaldasTalhaoLoading(false);
    }
  }, []);

  const getAtividadesByTalhao = useCallback(async (id, filter) => {
    try {
      setAtividadesByTalhaoLoading(true);
      const response = await api.talhao().getAtividadesByTalhao(id, filter);
      setAtividadesByTalhao(response);
      setAtividadesByTalhaoLoading(false);
    } catch (_) {
      setAtividadesByTalhaoLoading(false);
    }
  }, []);

  const addCoordLand = useCallback(async (id, data) => {
    try {
      setIsCoordLoading(true);
      const coord = {
        coords: data,
      };
      await api.talhao().addCoordTalhoes(id, coord);
      showToast({
        type: 'success',
        message: 'Coordenadas adicionadas com sucesso!',
      });
    } catch (err) {
      showToast(appWarnings.Danger(err));
      throw new Error();
    } finally {
      setIsCoordLoading(false);
    }
  }, []);

  const editCoordLand = useCallback(async (id, data) => {
    try {
      setIsCoordLoading(true);
      const coord = {
        coords: data,
      };
      await api.talhao().editCoordTalhoes(id, coord);
      showToast({
        type: 'success',
        message: 'Coordenadas editadas com sucesso!',
      });
    } catch (err) {
      showToast(appWarnings.Danger(err));
      throw new Error();
    } finally {
      setIsCoordLoading(false);
    }
  }, []);

  const delCoordTalhoes = useCallback(async ({ id, filter }) => {
    try {
      await api.talhao().delCoordTalhoes(id);
      showToast({
        type: 'success',
        message: 'Removido com sucesso!',
      });
      getTalhoes(filter);
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Não foi possível remover!',
      });
    }
  }, []);

  const relRegAtividadesByAtividade = useCallback(async (id, filter) => {
    try {
      setRelRegAtividadesLoading(true);
      const response = await api
        .talhao()
        .relRegAtividadesByAtividade(id, filter);

      setRelRegAtividades(response);

      setRelRegAtividadesLoading(false);
    } catch {
      setRelRegAtividadesLoading(false);
    }
  }, []);

  const getRelTalhoesDetalhesAtividades = useCallback(async (id, filter) => {
    try {
      setRelTalhoesDetalhesAtividadesLoading(true);
      const response = await api
        .relTalhoes()
        .getRelTalhoesDetalhesAtividades(id, filter);
      setRelTalhoesDetalhesAtividades(response);
      setRelTalhoesDetalhesAtividadesLoading(false);
    } catch {
      setRelTalhoesDetalhesAtividadesLoading(false);
    }
  }, []);

  const normalizeLinhasPaginadas = (response, queryParams = {}) => {
    const content = response?.content ?? [];
    const page = queryParams.page || response?.pagination?.page || 1;
    const pageSize =
      queryParams.pageSize ||
      queryParams.limit ||
      response?.pagination?.pageSize ||
      response?.pagination?.limit ||
      100;
    const total =
      response?.pagination?.total ?? response?.total ?? content.length;
    const totalPages =
      response?.pagination?.totalPages ??
      Math.max(1, Math.ceil(total / pageSize));

    const pagination = {
      page,
      pageSize,
      limit: pageSize,
      total,
      totalPages,
    };
    return { content, pagination };
  };

  const getLinhasTalhao = useCallback(async (params) => {
    try {
      setLinhasTalhaoLoading(true);
      const queryParams =
        typeof params === 'number' ? { talhaoId: params } : params;
      const response = await api.talhao().getTalhaoLinhas(queryParams);
      if (queryParams?.page || queryParams?.reduced === false) {
        setLinhasTalhaoPaginadas(normalizeLinhasPaginadas(response, queryParams));
      } else {
        setLinhasTalhao(response);
      }
    } catch (err) {
    } finally {
      setLinhasTalhaoLoading(false);
    }
  }, []);

  const getLinhasTalhaoAll = useCallback(async (talhaoId, { pageSize = 100 } = {}) => {
    try {
      setLinhasTalhaoLoading(true);
      let page = 1;
      let accumulated = [];
      let total = 0;

      do {
        // Busca sequencial de páginas até reunir todas as linhas do talhão
        // eslint-disable-next-line no-await-in-loop
        const response = await api.talhao().getTalhaoLinhas({
          talhaoId,
          page,
          pageSize,
          reduced: false,
        });
        const normalized = normalizeLinhasPaginadas(response, { page, pageSize });
        accumulated = accumulated.concat(normalized.content);
        total = normalized.pagination.total;
        page += 1;
      } while (accumulated.length < total);

      setLinhasTalhao(accumulated);
      return accumulated;
    } catch (err) {
      setLinhasTalhao([]);
      throw err;
    } finally {
      setLinhasTalhaoLoading(false);
    }
  }, []);

  const saveLinhasTalhao = useCallback(
    async (talhaoId, lines) => {
      try {
        setSaveLinhasTalhaoLoading(true);
        const data = lines
          .filter(({ code }) => String(code ?? '').trim().length > 0)
          .map(({ code, plants }) => ({
            code: String(code).trim(),
            plants: Number(plants) || 0,
          }));

        if (data.length > 300) {
          showToast({
            type: 'danger',
            message: 'O talhão pode ter no máximo 300 linhas.',
          });
          throw new Error('limite-linhas');
        }

        await api.talhao().saveLinhasTalhao(talhaoId, data);
        showToast({ ...success, message: 'Linhas do talhão salvas com sucesso!' });
      } catch (err) {
        const duplicados = err?.response?.data?.data?.duplicados;
        if (!duplicados?.length) {
          showToast(appWarnings.Danger(err));
        }
        throw err;
      } finally {
        setSaveLinhasTalhaoLoading(false);
      }
    },
    [showToast],
  );

  const updateLinhaTalhao = useCallback(
    async (id, data) => {
      try {
        setUpdateLinhaTalhaoLoading(true);
        await api.talhao().updateLinhaTalhao(id, data);
        showToast({ ...success, message: 'Linha atualizada com sucesso!' });
      } catch (err) {
        const duplicados = err?.response?.data?.data?.duplicados;
        if (!duplicados?.length) {
          showToast(appWarnings.Danger(err));
        }
        throw err;
      } finally {
        setUpdateLinhaTalhaoLoading(false);
      }
    },
    [showToast],
  );

  const deleteLinhaTalhao = useCallback(
    async (id) => {
      try {
        setDeleteLinhaTalhaoLoading(true);
        await api.talhao().deleteLinhaTalhao(id);
        showToast({ ...success, message: 'Linha excluída com sucesso!' });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error('trazendo error');
      } finally {
        setDeleteLinhaTalhaoLoading(false);
      }
    },
    [showToast],
  );

  const getHistoricoPlantasLinha = useCallback(async (id) => {
    try {
      setHistoricoPlantasLinhaLoading(true);
      const response = await api.talhao().getHistoricoPlantasLinha(id);
      setHistoricoPlantasLinha(response);
    } catch (err) {
    } finally {
      setHistoricoPlantasLinhaLoading(false);
    }
  }, []);

  const saveHistoricoPlantasLinha = useCallback(
    async (id, plants) => {
      try {
        setSaveHistoricoPlantasLinhaLoading(true);
        await api.talhao().saveHistoricoPlantasLinha(id, plants);
        showToast({ ...success, message: 'Quantidade de plantas atualizada com sucesso!' });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error('trazendo error');
      } finally {
        setSaveHistoricoPlantasLinhaLoading(false);
      }
    },
    [showToast],
  );

  const getRelTalhoesDetalhesDias = useCallback(async (id, filter) => {
    try {
      setRelTalhoesDetalhesDiasLoading(true);
      const response = await api
        .relTalhoes()
        .getRelTalhoesDetalhesDias(id, filter);
      setRelTalhoesDetalhesDias(response);
      setRelTalhoesDetalhesDiasLoading(false);
    } catch {
      setRelTalhoesDetalhesDiasLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuth) {
      down();
    } else {
      getTalhoesReduced({ safras: 2, gerais: 1 });
    }
  }, [isAuth, getTalhoesReduced]);

  const contextValue = useMemo(
    () => ({
      delCoordTalhoes,
      talhoesReduced,
      getTalhoes,
      talhoes,
      talhoesReducedCoords,
      talhoesLoading,
      getTalhao,
      talhao,
      talhaoLoading,
      getAtividadesByTalhao,
      atividadesByTalhao,
      atividadesByTalhaoLoading,
      insertTalhao,
      insertTalhaoTutorial,
      updateTalhao,
      insertTalhaoLoading,
      removeTalhao,
      getInsumosByTalhao,
      insumosTalhao,
      insumosTalhaoLoading,
      getMdobrasByTalhao,
      mdobrasTalhao,
      mdobrasTalhaoLoading,
      getCaldasByTalhao,
      caldasTalhao,
      caldasTalhaoLoading,
      talhaoRemoveLoading,
      getTalhoesReduced,
      talhoesReducedLoading,
      relRegAtividadesByAtividade,
      relRegAtividades,
      relRegAtividadesLoading,
      setRelRegAtividades,
      setTalhao,
      setTalhoesReducedCoords,
      getColheitaByTalhao,
      colheitaTalhao,
      colheitaTalhaoLoading,
      getColheitaSimplificadaByTalhao,
      colheitaSimplificadaTalhao,
      colheitaSimplificadaTalhaoLoading,
      addCoordLand,
      editCoordLand,
      isCoordLoading,
      getMaquinasByTalhao,
      maquinasTalhao,
      maquinasTalhaoLoading,
      getRelTalhoesDetalhesAtividades,
      relTalhoesDetalhesAtividades,
      relTalhoesDetalhesAtividadesLoading,
      getRelTalhoesDetalhesDias,
      relTalhoesDetalhesDias,
      relTalhoesDetalhesDiasLoading,
      getLinhasTalhao,
      getLinhasTalhaoAll,
      linhasTalhao,
      linhasTalhaoLoading,
      linhasTalhaoPaginadas,
      saveLinhasTalhao,
      saveLinhasTalhaoLoading,
      updateLinhaTalhao,
      updateLinhaTalhaoLoading,
      deleteLinhaTalhao,
      deleteLinhaTalhaoLoading,
      getHistoricoPlantasLinha,
      historicoPlantasLinha,
      historicoPlantasLinhaLoading,
      saveHistoricoPlantasLinha,
      saveHistoricoPlantasLinhaLoading,
    }),
    [
      delCoordTalhoes,
      talhoesReduced,
      getTalhoes,
      talhoes,
      talhoesReducedCoords,
      talhoesLoading,
      getTalhao,
      talhao,
      talhaoLoading,
      getAtividadesByTalhao,
      atividadesByTalhao,
      atividadesByTalhaoLoading,
      insertTalhao,
      insertTalhaoTutorial,
      updateTalhao,
      insertTalhaoLoading,
      removeTalhao,
      getInsumosByTalhao,
      insumosTalhao,
      insumosTalhaoLoading,
      getMdobrasByTalhao,
      mdobrasTalhao,
      mdobrasTalhaoLoading,
      getCaldasByTalhao,
      caldasTalhao,
      caldasTalhaoLoading,
      talhaoRemoveLoading,
      getTalhoesReduced,
      talhoesReducedLoading,
      relRegAtividadesByAtividade,
      relRegAtividades,
      relRegAtividadesLoading,
      setRelRegAtividades,
      setTalhao,
      setTalhoesReducedCoords,
      getColheitaByTalhao,
      colheitaTalhao,
      colheitaTalhaoLoading,
      getColheitaSimplificadaByTalhao,
      colheitaSimplificadaTalhao,
      colheitaSimplificadaTalhaoLoading,
      addCoordLand,
      editCoordLand,
      isCoordLoading,
      getMaquinasByTalhao,
      maquinasTalhao,
      maquinasTalhaoLoading,
      getRelTalhoesDetalhesAtividades,
      relTalhoesDetalhesAtividades,
      relTalhoesDetalhesAtividadesLoading,
      getRelTalhoesDetalhesDias,
      relTalhoesDetalhesDias,
      relTalhoesDetalhesDiasLoading,
      getLinhasTalhao,
      getLinhasTalhaoAll,
      linhasTalhao,
      linhasTalhaoLoading,
      linhasTalhaoPaginadas,
      saveLinhasTalhao,
      saveLinhasTalhaoLoading,
      updateLinhaTalhao,
      updateLinhaTalhaoLoading,
      deleteLinhaTalhao,
      deleteLinhaTalhaoLoading,
      getHistoricoPlantasLinha,
      historicoPlantasLinha,
      historicoPlantasLinhaLoading,
      saveHistoricoPlantasLinha,
      saveHistoricoPlantasLinhaLoading,
    ],
  );

  return (
    <TalhaoContext.Provider value={contextValue}>
      {children}
    </TalhaoContext.Provider>
  );
}

export function useTalhao() {
  const context = useContext(TalhaoContext);

  if (!context)
    throw new Error('useTalhao must be used within an AuthProvider');

  return context;
}
